import { LocalTeacherEmploymentType, TeacherFields } from '../../../redux/types'

import { PlaceType } from '../../../components/AutoComplete'
import { isDocExpired } from '../../../helpers/utils'
import {
    FieldState,
    TeacherAccountState,
    TeacherAccountState_v2,
} from '../../../firebase/types-teacher'
import styles from './index.module.scss'
import { userLocationSchema } from '../../../schemas/teacherReg'
import { FieldState_v2, UserLocationType } from '../../../schemas/teacherReg'

export const FAVOURITE_SUBJECT = [
    'Art',
    'Business Management',
    'Design Tech',
    'Digital Tech',
    'Drama',
    'English',
    'Food Tech',
    'Geography',
    'Health',
    'Humanities',
    'LOTE',
    'Maths',
    'Multimedia',
    'Music',
    'Outdoor Ed',
    'Performing Arts',
    'Physical Ed',
    'Science',
    'Visual Arts',
]

export const teacherEmploymentSettingText = (
    value?: LocalTeacherEmploymentType | null
) => {
    switch (value) {
        case 'both':
            return 'Schools and Early Childhood'
        case 'earlyChildcare':
            return 'Early Childhood'
        case 'school':
            return 'Schools'
        case 'supportStaff':
            return 'Education Support'
        default:
            return '-'
    }
}

export const toTitleCase = (str?: string) => {
    return str?.replace(
        /\w\S*/g,
        (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
    )
}

export const getLocation = (location: PlaceType | null): UserLocationType => {
    if (!location) {
        throw new Error('Invalid location')
    }
    let address = location.address_components
    let geometry = location.geometry
    // if (!address || !geometry) throw new Error('Invalid location')

    let countryObj = address?.filter((el) => el.types.includes('country'))[0]
    // if (!countryObj) throw new Error('Invalid location')
    let country = countryObj?.long_name

    let stateObj = address?.filter((el) =>
        el.types.includes('administrative_area_level_1')
    )[0]
    // if (!stateObj) throw new Error('Invalid location')
    let state = stateObj?.short_name

    let suburbObj = address?.filter((el) => el.types.includes('locality'))[0]
    // if (!suburbObj) throw new Error('Invalid location')
    let suburb = suburbObj?.long_name

    let postal_codeObj = address?.filter((el) =>
        el.types.includes('postal_code')
    )[0]
    // if (!postal_codeObj) throw new Error('Invalid location')
    let postal_code = postal_codeObj?.long_name

    let long_address =
        location.description ||
        location.formatted_address ||
        location.structured_formatting.secondary_text ||
        'No suitable address'

    return userLocationSchema.parse({
        address: long_address,
        country,
        homeState: state,
        lat: geometry?.location.lat(),
        long: geometry?.location.lng(),
        postCode: postal_code,
        suburb,
    })
}

export const getLocationSafe = (location: PlaceType | null) => {
    if (!location) {
        return
    }
    let address = location.address_components
    let geometry = location.geometry
    // if (!address || !geometry) throw new Error('Invalid location')

    let countryObj = address?.filter((el) => el.types.includes('country'))[0]
    // if (!countryObj) throw new Error('Invalid location')
    let country = countryObj?.long_name

    let stateObj = address?.filter((el) =>
        el.types.includes('administrative_area_level_1')
    )[0]
    // if (!stateObj) throw new Error('Invalid location')
    let state = stateObj?.short_name

    let suburbObj = address?.filter((el) => el.types.includes('locality'))[0]
    // if (!suburbObj) throw new Error('Invalid location')
    let suburb = suburbObj?.long_name

    let postal_codeObj = address?.filter((el) =>
        el.types.includes('postal_code')
    )[0]
    // if (!postal_codeObj) throw new Error('Invalid location')
    let postal_code = postal_codeObj?.long_name

    let long_address =
        location.description ||
        location.formatted_address ||
        location.structured_formatting.secondary_text ||
        null

    const coordinates = geometry?.location
        ? {
              lat: geometry.location.lat(),
              lng: geometry.location.lng(),
          }
        : null

    return {
        address: long_address,
        country: country || null,
        locationState: state || null,
        coordinates,
        postCode: postal_code || null,
        suburb: suburb || null,
        website: location.website || null,
    }
}

export const getAccreditationExpiryState = (
    docs: TeacherFields['accreditation']
) => {
    let expiredCount = 0
    for (let empSetting of Object.values(docs || {})) {
        for (let locState of Object.values(empSetting || {})) {
            expiredCount =
                expiredCount +
                Object.values(locState || {}).filter((doc) => {
                    // Check if the doc is mandatory

                    // Should we get the submitted (yet to be approved count as well?)
                    if (typeof doc !== 'string' && doc.expiryDate) {
                        return isDocExpired(doc.expiryDate as string)
                    } else {
                        return false
                    }
                }).length
        }
    }

    return expiredCount
}

export const renderAccordionText = (
    accountState: TeacherAccountState | undefined,
    fieldState: FieldState
) => {
    switch (accountState) {
        case TeacherAccountState.APPROVED: {
            switch (fieldState as FieldState) {
                case FieldState.APPROVED:
                    return 'Approved'
                case FieldState.SUBMITTED:
                    return 'Submitted'
                case FieldState.REJECTED:
                    return 'Rejected'
                case FieldState.EXPIRED:
                    return 'Expired'
                case FieldState.INCOMPLETE:
                default:
                    return 'Incomplete'
            }
        }
        case TeacherAccountState.SUBMITTED:
        case TeacherAccountState.REJECTED:
        case TeacherAccountState.ONHOLD: {
            switch (fieldState as FieldState) {
                case FieldState.SUBMITTED:
                case FieldState.APPROVED:
                    return 'Submitted'
                case FieldState.REJECTED:
                    return 'Rejected'
                case FieldState.EXPIRED:
                    return 'Expired'
                case FieldState.INCOMPLETE:
                default:
                    return 'Incomplete'
            }
        }
        case TeacherAccountState.INCOMPLETE:
        default: {
            switch (fieldState as FieldState) {
                case FieldState.SUBMITTED:
                case FieldState.APPROVED:
                    return ''
                case FieldState.REJECTED:
                    return 'Rejected'
                case FieldState.EXPIRED:
                    return 'Expired'
                case FieldState.INCOMPLETE:
                default:
                    return 'Incomplete'
            }
        }
    }
}

export const renderAccordionText_v2 = (
    accountState: TeacherAccountState_v2,
    fieldState: FieldState_v2
) => {
    switch (accountState) {
        case 'approved': {
            return toTitleCase(fieldState)
        }
        case 'submitted':
        case 'rejected':
        case 'onHold': {
            switch (fieldState) {
                case 'submitted':
                case 'approved':
                    return 'Submitted'
                case 'rejected':
                    return 'Rejected'
                case 'expired':
                    return 'Expired'
                case 'incomplete':
                default:
                    return 'Incomplete'
            }
        }
        case 'incomplete':
        default: {
            switch (fieldState) {
                case 'submitted':
                case 'approved':
                    return ''
                case 'rejected':
                    return 'Rejected'
                case 'expired':
                    return 'Expired'
                case 'incomplete':
                default:
                    return 'Incomplete'
            }
        }
    }
}

export const renderAccordionStyle = (fieldState: FieldState) => {
    switch (fieldState as FieldState) {
        case FieldState.SUBMITTED:
        case FieldState.APPROVED:
            return styles['accordion']
        case FieldState.EXPIRED:
        case FieldState.REJECTED:
            return styles['accordion--rejected']
        case FieldState.INCOMPLETE:
        default:
            return styles['accordion--incomplete']
    }
}

export const renderAccordionStyle_v2 = (fieldState: FieldState_v2) => {
    switch (fieldState) {
        case 'submitted':
        case 'approved':
            return styles['accordion']
        case 'rejected':
        case 'expired':
            return styles['accordion--rejected']
        case 'incomplete':
        default:
            return styles['accordion--incomplete']
    }
}

export const renderAccordionVariant_v2 = (
    fieldState: FieldState_v2,
    isReview?: boolean
): 'accountDefault' | 'accountRejected' | 'accountIncomplete' | 'review' => {
    if (isReview) {
        return 'review'
    }
    switch (fieldState) {
        case 'submitted':
        case 'approved':
            return 'accountDefault'
        case 'rejected':
        case 'expired':
            return 'accountRejected'
        case 'incomplete':
        default:
            return 'accountIncomplete'
    }
}

export const renderAccordionVariant = (
    fieldState: FieldState
): 'accountDefault' | 'accountRejected' | 'accountIncomplete' => {
    switch (fieldState as FieldState) {
        case FieldState.SUBMITTED:
        case FieldState.APPROVED:
            return 'accountDefault'
        case FieldState.EXPIRED:
        case FieldState.REJECTED:
            return 'accountRejected'
        case FieldState.INCOMPLETE:
        default:
            return 'accountIncomplete'
    }
}

export const renderAccordionColor = (fieldState: FieldState) => {
    switch (fieldState as FieldState) {
        case FieldState.SUBMITTED:
        case FieldState.APPROVED:
            return styles.secondaryBlue800
        case FieldState.EXPIRED:
        case FieldState.REJECTED:
            return styles.destructiveRed500
        case FieldState.INCOMPLETE:
        default:
            return styles.primaryYellow500
    }
}

export const renderAccordionColor_v2 = (fieldState: FieldState_v2) => {
    switch (fieldState) {
        case 'approved':
        case 'submitted':
            return styles.secondaryBlue800
        case 'expired':
        case 'rejected':
            return styles.destructiveRed500
        case 'incomplete':
        default:
            return styles.primaryYellow500
    }
}

export const canEditItem = (
    accountState: TeacherAccountState,
    fieldState: FieldState,
    empSettingState?: TeacherAccountState
) => {
    switch (accountState) {
        case TeacherAccountState.SUBMITTED:
        case TeacherAccountState.ONHOLD: {
            switch (fieldState) {
                case FieldState.SUBMITTED:
                case FieldState.APPROVED:
                    return false
                default:
                    return true
            }
        }
        case TeacherAccountState.APPROVED: {
            switch (fieldState) {
                case FieldState.APPROVED:
                case FieldState.SUBMITTED:
                    return empSettingState === TeacherAccountState.REJECTED
                case FieldState.REJECTED:
                case FieldState.EXPIRED:
                    return empSettingState !== TeacherAccountState.APPROVED
                default:
                    return true
            }
        }
        case TeacherAccountState.REJECTED:
        case TeacherAccountState.INCOMPLETE:
        default:
            return true
    }
}

export const canEditItem_v2 = (
    accountState: TeacherAccountState_v2,
    fieldState: FieldState_v2,
    empSettingState?: TeacherAccountState_v2
) => {
    switch (accountState) {
        case 'deleted':
        case 'offboarded': {
            return false
        }

        case 'submitted':
        case 'onHold': {
            switch (fieldState) {
                case 'submitted':
                case 'approved':
                    return false
                default:
                    return true
            }
        }
        case 'approved': {
            switch (fieldState) {
                case 'approved':
                case 'submitted':
                    return empSettingState === 'rejected'
                case 'rejected':
                case 'expired':
                    return empSettingState !== 'approved'
                default:
                    return true
            }
        }
        case 'rejected':
        case 'incomplete':
        default:
            return true
    }
}
